import React, {useEffect, useRef, useState} from "react";
import "./Search.scss";
                                            
import {getBtnTextColor} from "../helper/setTextColor";
import SearchIcon from "./icons/SearchIcon";
                                                                            
import {splitByMatches} from "../helper/searchPreview";
                                             
import {resolvePriceDisplay} from "../helper/priceDisplay";

                           
                              
                                 
                             
                                 
                                          
                               
                            
                       
                         
                
  

// below two characters a prefix narrows nothing down
const MINIMUM_TYPED_LENGTH = 2;

// one request per word rather than one per letter, and the preview stands by the time the customer looks up
const DEBOUNCE_MILLIS = 200;

const NOTHING_HIGHLIGHTED = -1;

const LISTBOX_ID = "ucl-searchPreview";

const optionId = (index        ) => `ucl-searchPreview-option-${index}`;

            
                                                   
                                         
                    

// The module runs for the one vehicle it is embedded for, so the preview offers no vehicles. The
// order the groups are shown in is the order the arrow keys walk them.
const entriesOf = (preview                )          => !preview || (preview.categories.length === 0 && preview.products.length === 0)
    ? []
    : [
        ...preview.categories.map(category => ({kind: "category", category})),
        ...preview.products.map(product => ({kind: "product", product})),
        {kind: "all"},
    ];

const Highlighted = ({text, typed}                               ) =>
    splitByMatches(text, typed).map((part, index) => part.isMatch
        ? <mark key={index}>{part.text}</mark>
        : <React.Fragment key={index}>{part.text}</React.Fragment>);

const CategoryIcon = () =>
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"
              fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>;

// the picture of a category where there is one, the icon of its group where there is none
const EntryPicture = ({imageUrl, icon}                                ) => imageUrl
    ? <span className="search-preview-thumbnail cover"><img src={imageUrl} alt=""/></span>
    : <span className="search-preview-icon">{icon}</span>;

const productImageOf = (product         )          => {
    const galleryImage = product.galleryImage || {};

    return (galleryImage.previewImage || {}).attachmentUrl || galleryImage.attachmentUrl;
};

/**
 * The search box and the preview it opens while typing: the categories the typed words lead to, the
 * first products, and the way to all results. The preview comes from the very search the result page
 * runs, so what it shows is what the customer gets.
 *
 * <p>Only typing asks for a preview. Picking an entry and submitting both end the typing, and neither
 * may bring the preview back; the typed ref keeps that apart without being a reason to render.
 */
const Search = (props             ) => {
    const [fuzzy, setFuzzy] = useState(props.searchPhrase || "");
    const [isPreviewOpen, setPreviewOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(NOTHING_HIGHLIGHTED);
    const typed = useRef(null);
    const scheduledRequest = useRef(null);

    const entries = isPreviewOpen ? entriesOf(props.preview) : [];

    useEffect(() => {
        if (typed.current === null) {
            return () => {};
        }

        const value = typed.current.trim();

        if (value.length < MINIMUM_TYPED_LENGTH) {
            props.clearPreview();

            return () => {};
        }

        scheduledRequest.current = setTimeout(() => props.fetchPreview(value), DEBOUNCE_MILLIS);

        return () => clearTimeout(scheduledRequest.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fuzzy]);

    const toggleSearch = e => {
        e.preventDefault();
        const node = document.getElementById("sl-content-root");
        if (node) {
            node.classList.toggle("search-on");
        }
    };

    const closePreview = () => {
        // a search submitted within the debounce must not be followed by the preview it had scheduled
        clearTimeout(scheduledRequest.current);
        typed.current = null;
        setPreviewOpen(false);
        setHighlighted(NOTHING_HIGHLIGHTED);
        props.clearPreview();
    };

    const searchFor = (phrase        ) => {
        closePreview();

        if (phrase !== null && phrase.length > 0) {
            props.setSearch(phrase);
        }
    };

    const choose = (entry       ) => {
        if (entry.kind === "all") {
            searchFor(fuzzy);

            return;
        }

        closePreview();
        setFuzzy("");

        if (entry.kind === "category") {
            props.openCategory(entry.category);
        } else {
            props.openProduct(entry.product);
        }
    };

    const onChange = (value        ) => {
        typed.current = value;
        setPreviewOpen(true);
        setHighlighted(NOTHING_HIGHLIGHTED);
        setFuzzy(value);
    };

    const handleSearch = e => {
        e.preventDefault();

        if (entries[highlighted]) {
            choose(entries[highlighted]);

            return;
        }

        searchFor(fuzzy);
    };

    const moveHighlight = (by        ) => {
        const next = highlighted + by;

        setHighlighted(next < 0 || next >= entries.length ? NOTHING_HIGHLIGHTED : next);
    };

    const onKeyDown = event => {
        if (event.key === "Escape") {
            closePreview();

            return;
        }

        if (entries.length > 0 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            // or the caret would jump to the far end of what has been typed
            event.preventDefault();
            moveHighlight(event.key === "ArrowDown" ? 1 : -1);
        }
    };

    // onMouseDown and not onClick: the click comes after the input has lost focus, and the preview
    // would be gone before it arrives
    const optionProps = (index        , modifier        ) => ({
        id: optionId(index),
        role: "option",
        "aria-selected": index === highlighted,
        className: `search-preview-option ${modifier}` + (index === highlighted ? " highlighted" : ""),
        onMouseDown: event => {
            event.preventDefault();
            choose(entries[index]);
        },
        onMouseEnter: () => setHighlighted(index),
    });

    const renderGroup = (kind        , heading        , render                                      ) => {
        const groupEntries = entries
            .map((entry, index) => ({entry, index}))
            .filter(({entry}) => entry.kind === kind);

        if (groupEntries.length === 0) {
            return null;
        }

        const headingId = `${LISTBOX_ID}-${kind}`;

        return <div role="group" aria-labelledby={headingId} className="search-preview-group">
            <div id={headingId} role="presentation" className="search-preview-heading">{heading}</div>
            {groupEntries.map(({entry, index}) => render(entry, index))}
        </div>;
    };

    const totalItems = props.preview ? props.preview.totalItems : 0;

    return <React.Fragment>
	<div className={"search" + (entries.length > 0 ? " suggestions-on" : "")}>
		<div className="control">
			<span>
				{
					<button className="toggle-search" style={{color: getBtnTextColor()}}
                                onClick={e => toggleSearch(e)}>
						{props.t("navigation.searching.close")}
					</button>
                    }
			</span>
			<span>
				{
					<button className="toggle-megasearch" style={{display: "none"}}>
						{props.t("navigation.searching.expand")}
					</button>
                    }

			</span>
		</div>
		<div className="scroll">
			<form onSubmit={e => handleSearch(e)} role="search">
				<div className="searchbox">
					<input type="search" className="searchTerm" onChange={e => onChange(e.target.value)}
                               onKeyDown={onKeyDown}
                               onBlur={() => isPreviewOpen && closePreview()}
                               value={fuzzy}
                               autoComplete="off"
                               role="combobox"
                               aria-expanded={entries.length > 0}
                               aria-autocomplete="list"
                               aria-controls={LISTBOX_ID}
                               aria-activedescendant={entries[highlighted] ? optionId(highlighted) : undefined}
                               placeholder={props.t("navigation.searching.placeholder")}/>
					<button aria-label={props.t("navigation.searching.submit")} className={"submit-button"}
                                type={"submit"}>
						<SearchIcon/>
					</button>
				</div>
				{entries.length > 0 &&
					<div className="search-preview" id={LISTBOX_ID} role="listbox"
                         aria-label={props.t("navigation.searching.suggestions")}>
						{renderGroup("category", props.t("navigation.searching.categories"), (entry, index) =>
                            entry.kind === "category" &&
							<div key={entry.category.key} {...optionProps(index, "category")}>
								<EntryPicture imageUrl={entry.category.imageUrl} icon={<CategoryIcon/>}/>
								<span className="search-preview-text">
									<span className="search-preview-title"><Highlighted text={entry.category.title} typed={fuzzy}/></span>
									{entry.category.count != null &&
										<span className="search-preview-meta">{props.t("navigation.searching.productCount", {count: entry.category.count})}</span>}
								</span>
							</div>)}
						{renderGroup("product", props.t("navigation.searching.products"), (entry, index) => {
                            if (entry.kind !== "product") return null;

                            const {product} = entry;
                            const image = productImageOf(product);
                            const {price} = resolvePriceDisplay(product);

                            return <div key={product.id} {...optionProps(index, "product")}>
								<span className="search-preview-thumbnail">{image && <img src={image} alt=""/>}</span>
								<span className="search-preview-text">
									<span className="search-preview-title"><Highlighted text={product.name} typed={fuzzy}/></span>
									{props.showPrice && price &&
										<span className="search-preview-meta">{props.formatPrice(price)}</span>}
								</span>
							</div>;
                        })}
						<div {...optionProps(entries.length - 1, "all")}>
							{props.t("navigation.searching.showAll", {count: totalItems})}
						</div>
					</div>}
			</form>

			<div className="megasearch" style={{display: "none"}}>
				<input type="checkbox" name="search-cat" id="search-cat-00"/>
				<label htmlFor="search-cat-00">
					<span>
						TOP Angebote
					</span>
					<span><small>0000</small></span>
				</label>
				<input type="checkbox" name="search-cat" id="search-cat-01"/>
				<label htmlFor="search-cat-01">
					<span>
						Packen und Laden
					</span>
					<span><small>0000</small></span>
				</label>
			</div>

		</div>
		<div className="action">
			{
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
				<a className="btn toggle-search">
					000 {props.t("navigation.searching.show")}
				</a>
                }
		</div>
	</div>
    </React.Fragment>;
};

export default Search;
